import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Eye, Download, Edit, Repeat } from "lucide-react";
import { toast } from "react-hot-toast";
import Modal from "react-modal";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import mangrishLogo from "../assets/logo.jpeg";
import princePipesLogo from "../assets/images/prince2.jpeg";
import princePipesLogo1 from "../assets/images/prince1.jpeg";
import astral1 from "../assets/images/astral1.jpeg";
import astra2 from "../assets/images/astral2.jpeg";
// Set the app element for accessibility
Modal.setAppElement("#root");

// Extend jsPDF for autoTable
declare module "jspdf" {
  interface jsPDF {
    lastAutoTable: {
      finalY: number;
    };
    autoTable: (options: any) => jsPDF;
  }
}

interface Quotation {
  _id: string;
  quotationNo: string;
  date: string;
  billTo: {
    name: string;
    address: string;
    email: string;
    phone: string;
  };
  shipTo?: {
    name: string;
    address: string;
    email: string;
    phone: string;
  };
  products: {
    name: string;
    size: string;
    qty: number;
    unit: string;
    mrp: number;
    discount1: number;
    discount2: number;
    discount: number;
    rate: number;
    amount: number;
    hsncode: string;
    gstpercentage: number;
  }[];
  note?: string;
  terms: string[];
  bankDetails: {
    name: string;
    accountNo: string;
    branch: string;
    ifsc: string;
  };
  transportation: number;
  totalgstPercentage: number;
  type?: string;
  mangrishLogo?: string;
  princePipesLogo?: string;
  princePipesLogo1?: string;
  gstNumber?: string;
}

const QuotationHistory: React.FC = () => {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const storedUser = localStorage.getItem("user");
  const userEmail = storedUser ? JSON.parse(storedUser).email : "";

  const fetchQuotations = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/quotations/quotation`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("res.data", res.data?.quotations);

      setQuotations(res.data?.quotations);
    } catch (err) {
      console.error("Failed to fetch quotations", err);
      toast.error("Failed to load quotations");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const handleDownloadPDF = (quotation: Quotation) => {
    console.log("Quotation", quotation);

    try {
      const calculateSubtotal = () => {
        return quotation.products.reduce((sum, p) => sum + (p.amount || 0), 0);
      };

      const calculateGst = () => {
        const subtotal = calculateSubtotal();
        return subtotal * (quotation.totalgstPercentage / 100);
      };

      const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        const gst = calculateGst();
        return subtotal + gst + (quotation.transportation || 0);
      };

      const doc = new jsPDF();

      // Add logos if available
      if (princePipesLogo1)
        doc.addImage(
          quotation.gstNumber === "29ACAFM2729C1ZG"
            ? princePipesLogo1
            : astral1,
          "PNG",
          10,
          8,
          45,
          45
        );
      if (princePipesLogo)
        doc.addImage(
          quotation.gstNumber === "29ACAFM2729C1ZG" ? princePipesLogo : astra2,
          "JPEG",
          155,
          8,
          45,
          45
        );
      if (mangrishLogo) doc.addImage(mangrishLogo, "WEBP", 93, 6, 25, 25);

      // Company header
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(53, 194, 240);
      doc.text("MANGRISH", 105, 35, { align: "center" });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text("No. 7/4, 60 feet Road ,Gangaborewell Road,", 105, 47, {
        align: "center",
      });
      doc.text(
        "Amco Layout, Kotihosahalli, Kodigehalli Bengalore 560097",
        105,
        52,
        { align: "center" }
      );

      doc.setFont("helvetica", "normal");
      doc.text(
        `Authorised Distributor M/s ${
          quotation.gstNumber === "29ACAFM2729C1ZG"
            ? "Prince Pipes and Fittings Limited"
            : "Astral Ltd- for Infrastructure Range"
        }`,
        105,
        42,
        { align: "center" }
      );
      doc.text(`GST No. : ${quotation.gstNumber || ""}`, 105, 57, {
        align: "center",
      });

      doc.setTextColor(21, 4, 252);
      doc.text(
        `Ph No.: 9845068745    E-Mail : ${
          quotation.gstNumber === "29ACAFM2729C1ZG"
            ? "mp@mangrishprince.com"
            : "mangrish123@gmail.com"
        }, ${userEmail}`,
        105,
        62,
        { align: "center" }
      );

      // Quotation header
      doc.setFillColor(0, 51, 102);
      doc.rect(0, 65, 210, 10, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("QUOTATION", 105, 72, { align: "center" });
      doc.setTextColor(0, 0, 0);

      // Bill To section
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Bill To", 15, 82);
      doc.setFont("helvetica", "normal");
      doc.text(`${quotation?.billTo?.name}`, 15, 87);
      const addressLines = quotation.billTo.address.split("\n");
      addressLines.forEach((line, i) => doc.text(line, 15, 92 + i * 5));
      const emailY = 92 + addressLines.length * 5;
      doc.text(`Email:- ${quotation.billTo.email}`, 15, emailY);
      doc.text(`Ph no. ${quotation.billTo.phone}`, 15, emailY + 5);

      // Date and Quotation No
      const date = new Date(quotation.date);
      const formattedDate = date.toLocaleDateString("en-GB");
      doc.text(`Date :- ${formattedDate}`, 160, 82);
      // doc.text(`Date :- ${quotation.date}`, 160, 82);
      doc.text(`QTNO. ${quotation.quotationNo}`, 160, 87);

      // Ship To section if exists
      let shipToY = emailY + 15;
      if (quotation.shipTo?.name) {
        doc.setFont("helvetica", "bold");
        doc.text("Ship TO", 15, shipToY);
        doc.setFont("helvetica", "normal");
        doc.text(quotation.shipTo.name, 15, shipToY + 5);
        const shipAddressLines = quotation.shipTo.address.split("\n");
        shipAddressLines.forEach((line, i) => {
          doc.text(line, 15, shipToY + 10 + i * 5);
        });
        shipToY += 15 + shipAddressLines.length * 5;
      }

      // Products table
      const tableData = quotation?.products?.map((product, index) => [
        `${index + 1}`,
        `${product.size} ${product?.name} `,
        product.hsncode || '',
        product.qty.toLocaleString(),
        product.unit,
        product.mrp.toFixed(0),
        `${product.discount || 0}%`,
        product.rate.toFixed(2),
        product.amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
      ]);

      autoTable(doc, {
        startY: shipToY,
        head: [
          [
            { content: "Sl No", rowSpan: 1 },
            { content: "Prince Pipes", rowSpan: 1 },
            { content: "HSN Code", rowSpan: 1 },
            { content: "Qty", rowSpan: 1 },
            { content: "Unit", rowSpan: 1 },
            { content: "MRP", rowSpan: 1 },
            { content: "Disc%", rowSpan: 1 },
            { content: "Rate", rowSpan: 1 },
            { content: "Amount", rowSpan: 1 },
          ],
        ],
        body: tableData,
        headStyles: {
          fillColor: [255, 255, 4],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
          lineColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        bodyStyles: {
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          valign: "middle",
        },
        styles: {
          fontSize: 9,
          cellPadding: 2,
          overflow: "linebreak",
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        margin: { left: 15, right: 10 },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 45 },
          2: { cellWidth: 20, halign: "center" },
          3: { cellWidth: 15, halign: "center" },
          4: { cellWidth: 15, halign: "center" },
          5: { cellWidth: 15, halign: "center" },
          6: { cellWidth: 15, halign: "center" },
          7: { cellWidth: 15, halign: "right" },
          8: { cellWidth: 20, halign: "right" },
        },
      });

      const finalY = doc.lastAutoTable.finalY;
      const subtotal = calculateSubtotal();
      const gst = calculateGst();
      const total = calculateTotal();

      autoTable(doc, {
        startY: finalY + 0,
        body: [
          [
            "Transportation",
            quotation.transportation
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ","),
          ],
          [
            "Sub total",
            subtotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
          ],
          [
            `GST ${quotation.totalgstPercentage}%`,
            gst.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
          ],
          ["Total", total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")],
        ],
        styles: {
          fontSize: 9,
          cellPadding: 2,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        columnStyles: {
          0: {
            cellWidth: 40,
            fontStyle: "bold",
            halign: "right",
            fillColor: [220, 230, 255],
          },
          1: {
            cellWidth: 25,
            halign: "right",
            fillColor: [220, 230, 255],
          },
        },
        margin: { left: 130 },
        tableWidth: 55,
      });

      if (quotation.note) {
        doc.setFont("helvetica", "bold");
        doc.text(`Note: - ${quotation.note}`, 15, finalY + 35);
      }

      doc.setFont("helvetica", "bold");
      doc.text("Terms & conditions", 15, finalY + 45);
      doc.setFont("helvetica", "normal");
      quotation.terms.forEach((term, i) =>
        doc.text(`${i + 1}. ${term}`, 15, finalY + 50 + i * 5)
      );

      const bankDetailsY = finalY + 55 + quotation.terms.length * 5;
      doc.setFont("helvetica", "bold");
      doc.text("Bank Details", 15, bankDetailsY);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Payment favoring to: ${quotation?.bankDetails?.name}`,
        15,
        bankDetailsY + 5
      );
      doc.text(`Bank Name: CENTRAL BANK OF INDIA`, 15, bankDetailsY + 10);
      doc.text(
        `A/C No. : ${quotation.bankDetails?.accountNo}`,
        15,
        bankDetailsY + 15
      );
      doc.text(
        `Branch : ${quotation.bankDetails?.branch}`,
        15,
        bankDetailsY + 20
      );
      doc.text(
        `IFSC Code : ${quotation.bankDetails?.ifsc}`,
        15,
        bankDetailsY + 25
      );

      // Signature
      doc.text(`For Mangrish,`, 140, bankDetailsY);
      const signatureY = bankDetailsY + 15;
      doc.setDrawColor(0);
      doc.line(140, signatureY, 190, signatureY);
      doc.setFontSize(10);
      doc.text("Authoraised Signatory", 140, signatureY + 5);
      doc.text("This is system genereated signature is not require", 140, signatureY + 10);

      // Save the PDF
      doc.save(`Quotation_${quotation.quotationNo}.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF", err);
      toast.error("Failed to generate PDF");
    }
  };

  const handleConvertToPI = async (id: string) => {
    if (!window.confirm("Convert this quotation to Proforma Invoice?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/quotations/${id}/convert-to-pi`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Converted to Proforma Invoice");
      fetchQuotations();
    } catch (err) {
      toast.error("Conversion failed");
      console.error(err);
    }
  };

  const handleViewQuotation = (quotation: Quotation) => {
    setSelectedQuotation(quotation);
    setIsModalOpen(true);
  };

  const calculateProductTotal = (products: any[]) => {
    return products.reduce((sum, p) => sum + p.amount, 0);
  };

  const calculateSubtotal = (products: any[]) => {
    const productTotal = calculateProductTotal(products);
    const discount = 0; // Discount is now manually entered at quotation level
    return productTotal - discount;
  };

  const calculateGst = (products: any[], totalgstPercentage: number) => {
    const subtotal = calculateSubtotal(products);
    return subtotal * (totalgstPercentage / 100);
  };

  const calculateTotal = (quotation: Quotation) => {
    const subtotal = calculateSubtotal(quotation.products);
    const gst = calculateGst(quotation.products, quotation.totalgstPercentage);
    return subtotal + gst + (quotation.transportation || 0);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">Quotation History</h1>

      <div className="overflow-x-auto">
        <table className="w-full table-auto border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 border">#</th>
              <th className="p-3 border">Quotation No</th>
              <th className="p-3 border">Customer</th>
              <th className="p-3 border">Date</th>
              <th className="p-3 border">Total Amount</th>
              <th className="p-3 border">Type</th>
              <th className="p-3 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {quotations.map((q, i) => (
              <tr key={q._id} className="text-sm">
                <td className="p-3 border text-center">{i + 1}</td>
                <td className="p-3 border">{q.quotationNo}</td>
                <td className="p-3 border">{q.billTo?.name}</td>
                <td className="p-3 border">
                  {new Date(q.date).toLocaleDateString()}
                </td>
                <td className="p-3 border text-right">
                  ₹{calculateTotal(q).toFixed(2)}
                </td>
                <td className="p-3 border">
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      q.type === "pi"
                        ? "bg-orange-100 text-orange-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {q.type === "pi" ? "Proforma Invoice" : "Quotation"}
                  </span>
                </td>
                <td className="p-3 border space-x-2">
                  <button
                    onClick={() => handleViewQuotation(q)}
                    className="text-blue-600 hover:text-blue-800"
                    title="View"
                  >
                    <Eye size={16} />
                  </button>

                  <button
                    onClick={() => handleDownloadPDF(q)}
                    className="text-green-600 hover:text-green-800"
                    title="Download PDF"
                  >
                    <Download size={16} />
                  </button>

                  <button
                    onClick={() => navigate(`/quotation/edit/${q?._id}`)}
                    className="text-gray-700 hover:text-black"
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>

                  {q.type !== "pi" && (
                    <button
                      onClick={() => handleConvertToPI(q._id)}
                      className="text-orange-500 hover:text-orange-700"
                      title="Convert to PI"
                    >
                      <Repeat size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {quotations.length === 0 && !isLoading && (
          <p className="text-center text-gray-500 py-8">No quotations found.</p>
        )}
      </div>

      {/* View Quotation Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        className="modal max-w-4xl w-full bg-white rounded-lg shadow-xl overflow-hidden max-h-[90vh]"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      >
        {selectedQuotation ? (
          <div className="p-6 overflow-y-auto max-h-[80vh]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {selectedQuotation.type === "pi"
                  ? "Proforma Invoice"
                  : "Quotation"}{" "}
                - {selectedQuotation.quotationNo}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="border-b pb-4 mb-4">
              <div className="flex justify-between">
                <div>
                  <h3 className="font-bold">Bill To:</h3>
                  <p>{selectedQuotation.billTo?.name || "N/A"}</p>
                  <p className="whitespace-pre-line">
                    {selectedQuotation.billTo?.address || ""}
                  </p>
                  <p>Email: {selectedQuotation.billTo?.email || "N/A"}</p>
                  <p>Phone: {selectedQuotation.billTo?.phone || "N/A"}</p>
                </div>
                <div className="text-right">
                  <p>
                    <span className="font-bold">Date:</span>{" "}
                    {new Date(selectedQuotation.date).toLocaleDateString()}
                  </p>
                  <p>
                    <span className="font-bold">Quotation No:</span>{" "}
                    {selectedQuotation.quotationNo}
                  </p>
                </div>
              </div>

              {selectedQuotation.shipTo?.name && (
                <div className="mt-4">
                  <h3 className="font-bold">Ship To:</h3>
                  <p>{selectedQuotation.shipTo.name}</p>
                  <p className="whitespace-pre-line">
                    {selectedQuotation.shipTo.address}
                  </p>
                  <p>Email: {selectedQuotation.shipTo.email}</p>
                  <p>Phone: {selectedQuotation.shipTo.phone}</p>
                </div>
              )}
            </div>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 border text-left">#</th>
                    <th className="p-2 border text-left">Product</th>
                    <th className="p-2 border text-center">HSN Code</th>
                    <th className="p-2 border text-center">Qty</th>
                    <th className="p-2 border text-center">Unit</th>
                    <th className="p-2 border text-center">MRP</th>
                    <th className="p-2 border text-right">Rate</th>
                    <th className="p-2 border text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedQuotation.products.map((product, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-2 border text-center">{index + 1}</td>
                      <td className="p-2 border">
                        {product.size} {product.name}
                        {/* {product.size && (
                          <div className="text-xs text-gray-500">
                           
                          </div>
                        )} */}
                      </td>
                      <td className="p-2 border text-center">{product.hsncode}</td>
                      <td className="p-2 border text-center">{product.qty}</td>
                      <td className="p-2 border text-center">{product.unit}</td>
                      <td className="p-2 border text-center">
                        {product.mrp.toFixed(2)}
                      </td>
                      <td className="p-2 border text-right">
                        {product.rate.toFixed(2)}
                      </td>
                      <td className="p-2 border text-right">
                        {product.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mb-4">
              <div className="w-64 text-sm">
                <div className="flex justify-between py-1">
                  <span className="font-bold">Product Total:</span>
                  <span>₹{calculateProductTotal(selectedQuotation.products).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-1 border-t border-gray-300">
                  <span className="font-bold">Subtotal:</span>
                  <span>
                    ₹{calculateSubtotal(selectedQuotation.products).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="font-bold">
                    GST {selectedQuotation.totalgstPercentage}%:
                  </span>
                  <span>
                    ₹
                    {calculateGst(
                      selectedQuotation.products,
                      selectedQuotation.totalgstPercentage
                    ).toFixed(2)}
                  </span>
                </div>
                {selectedQuotation.transportation > 0 && (
                  <div className="flex justify-between py-1">
                    <span className="font-bold">Transportation:</span>
                    <span>₹{selectedQuotation.transportation.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between py-1 border-t border-gray-300 font-bold">
                  <span>Total:</span>
                  <span>₹{calculateTotal(selectedQuotation).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {selectedQuotation.note && (
              <div className="mb-4 text-sm">
                <p className="font-bold">Note:</p>
                <p>{selectedQuotation.note}</p>
              </div>
            )}

            <div className="mb-4 text-sm">
              <h3 className="font-bold">Terms & Conditions:</h3>
              <ul className="list-disc pl-5">
                {selectedQuotation.terms.map((term, index) => (
                  <li key={index} className="py-1">
                    {term}
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-sm">
              <h3 className="font-bold">Bank Details:</h3>
              <p>
                Payment favoring to: "
                {selectedQuotation.bankDetails?.name || "N/A"}"
              </p>
              <p>Bank Name: CENTRAL BANK OF INDIA</p>
              <p>
                A/C No.: {selectedQuotation.bankDetails?.accountNo || "N/A"}
              </p>
              <p>Branch: {selectedQuotation.bankDetails?.branch || "N/A"}</p>
              <p>IFSC Code: {selectedQuotation.bankDetails?.ifsc || "N/A"}</p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center">Loading quotation details...</div>
        )}
      </Modal>
    </div>
  );
};

export default QuotationHistory;
// // src/pages/QuotationHistory.tsx
// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import { Eye, Download, Edit, Repeat } from "lucide-react";
// import { toast } from "react-hot-toast";
// import Modal from "react-modal";

// // Set the app element for accessibility
// Modal.setAppElement('#root');

// interface Quotation {
//   _id: string;
//   quotationNo: string;
//   date: string;
//   billTo: {
//     name: string;
//     address: string;
//     email: string;
//     phone: string;
//   };
//   shipTo?: {
//     name: string;
//     address: string;
//     email: string;
//     phone: string;
//   };
//   products: {
//     name: string;
//     size: string;
//     qty: number;
//     unit: string;
//     mrp: number;
//     discount1: number;
//     discount2: number;
//     rate: number;
//     amount: number;
//     hsncode: string;
//     gstpercentage: number;
//   }[];
//   note?: string;
//   terms: string[];
//   bankDetails: {
//     name: string;
//     accountNo: string;
//     branch: string;
//     ifsc: string;
//   };
//   transportation: number;
//   totalgstPercentage: number;
//   type?: string; // 'quotation' | 'pi'
// }

// const QuotationHistory: React.FC = () => {
//   const [quotations, setQuotations] = useState<Quotation[]>([]);
//   const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isLoading, setIsLoading] = useState(false);
//   const navigate = useNavigate();

//   const fetchQuotations = async () => {
//     try {
//       setIsLoading(true);
//       const token = localStorage.getItem("token");
//       const res = await axios.get("https://api.mangrishprince.com/api/v1/quotations", {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setQuotations(res.data);
//     } catch (err) {
//       console.error("Failed to fetch quotations", err);
//       toast.error("Failed to load quotations");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchQuotations();
//   }, []);

//   const handleDownloadPDF = async (id: string) => {
//     try {
//       const token = localStorage.getItem("token");
//       const res = await axios.get(
//         `https://api.mangrishprince.com/api/v1/quotations/${id}/pdf`,
//         {
//           responseType: "blob",
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       const blob = new Blob([res.data], { type: "application/pdf" });
//       const link = document.createElement("a");
//       link.href = window.URL.createObjectURL(blob);
//       link.download = `Quotation_${id}.pdf`;
//       link.click();
//     } catch (err) {
//       console.error("Failed to download PDF", err);
//       toast.error("Failed to download PDF");
//     }
//   };

//   const handleConvertToPI = async (id: string) => {
//     if (!window.confirm("Convert this quotation to Proforma Invoice?")) return;

//     try {
//       const token = localStorage.getItem("token");
//       await axios.put(
//         `https://api.mangrishprince.com/api/v1/quotations/${id}/convert-to-pi`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       toast.success("Converted to Proforma Invoice");
//       fetchQuotations();
//     } catch (err) {
//       toast.error("Conversion failed");
//       console.error(err);
//     }
//   };

//   const handleViewQuotation = (quotation: Quotation) => {
//     setSelectedQuotation(quotation);
//     setIsModalOpen(true);
//   };

//   const calculateSubtotal = (products: any[]) => {
//     return products.reduce((sum, p) => sum + (p.amount || 0), 0);
//   };

//   const calculateGst = (products: any[], totalgstPercentage: number) => {
//     const subtotal = calculateSubtotal(products);
//     return subtotal * (totalgstPercentage / 100);
//   };

//   const calculateTotal = (quotation: Quotation) => {
//     const subtotal = calculateSubtotal(quotation.products);
//     const gst = calculateGst(quotation.products, quotation.totalgstPercentage);
//     return subtotal + gst + (quotation.transportation || 0);
//   };

//   if (isLoading) {
//     return (
//       <div className="max-w-7xl mx-auto px-4 py-6 flex justify-center items-center h-64">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   return (
//     <div className="max-w-7xl mx-auto px-4 py-6">
//       <h1 className="text-2xl font-bold mb-6">Proforma Invoice (PI) / Quotation History</h1>

//       <div className="overflow-x-auto">
//         <table className="w-full table-auto border border-gray-300">
//           <thead className="bg-gray-100">
//             <tr>
//               <th className="p-3 border">#</th>
//               <th className="p-3 border">Quotation No</th>
//               <th className="p-3 border">Customer</th>
//               <th className="p-3 border">Date</th>
//               <th className="p-3 border">Total Amount</th>
//               <th className="p-3 border">Type</th>
//               <th className="p-3 border">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {quotations.map((q, i) => (
//               <tr key={q._id} className="text-sm">
//                 <td className="p-3 border text-center">{i + 1}</td>
//                 <td className="p-3 border">{q.quotationNo}</td>
//                 <td className="p-3 border">{q.billTo?.name}</td>
//                 <td className="p-3 border">{new Date(q.date).toLocaleDateString()}</td>
//                 <td className="p-3 border text-right">₹{calculateTotal(q).toFixed(2)}</td>
//                 <td className="p-3 border">
//                   <span
//                     className={`px-2 py-1 rounded text-xs font-semibold ${
//                       q.type === "pi" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"
//                     }`}
//                   >
//                     {q.type === "pi" ? "Proforma Invoice" : "Quotation"}
//                   </span>
//                 </td>
//                 <td className="p-3 border space-x-2">
//                   <button
//                     onClick={() => handleViewQuotation(q)}
//                     className="text-blue-600 hover:text-blue-800"
//                     title="View"
//                   >
//                     <Eye size={16} />
//                   </button>

//                   <button
//                     onClick={() => handleDownloadPDF(q._id)}
//                     className="text-green-600 hover:text-green-800"
//                     title="Download PDF"
//                   >
//                     <Download size={16} />
//                   </button>

//                   <button
//                     onClick={() => navigate(`/quotation/edit/${q._id}`)}
//                     className="text-gray-700 hover:text-black"
//                     title="Edit"
//                   >
//                     <Edit size={16} />
//                   </button>

//                   {q.type !== "pi" && (
//                     <button
//                       onClick={() => handleConvertToPI(q._id)}
//                       className="text-orange-500 hover:text-orange-700"
//                       title="Convert to PI"
//                     >
//                       <Repeat size={16} />
//                     </button>
//                   )}
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>

//         {quotations.length === 0 && !isLoading && (
//           <p className="text-center text-gray-500 py-8">No quotations found.</p>
//         )}
//       </div>

//       {/* View Quotation Modal */}
//       <Modal
//         isOpen={isModalOpen}
//         onRequestClose={() => setIsModalOpen(false)}
//         className="modal max-w-4xl w-full bg-white rounded-lg shadow-xl overflow-hidden"
//         overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
//       >
//         {selectedQuotation ? (
//           <div className="p-6">
//             <div className="flex justify-between items-center mb-4">
//               <h2 className="text-xl font-bold">
//                 {selectedQuotation.type === "pi" ? "Proforma Invoice" : "Quotation"} - {selectedQuotation.quotationNo}
//               </h2>
//               <button
//                 onClick={() => setIsModalOpen(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 ✕
//               </button>
//             </div>

//             <div className="border-b pb-4 mb-4">
//               <div className="flex justify-between">
//                 <div>
//                   <h3 className="font-bold">Bill To:</h3>
//                   <p>{selectedQuotation.billTo?.name || 'N/A'}</p>
//                   <p className="whitespace-pre-line">{selectedQuotation.billTo?.address || ''}</p>
//                   <p>Email: {selectedQuotation.billTo?.email || 'N/A'}</p>
//                   <p>Phone: {selectedQuotation.billTo?.phone || 'N/A'}</p>
//                 </div>
//                 <div className="text-right">
//                   <p><span className="font-bold">Date:</span> {new Date(selectedQuotation.date).toLocaleDateString()}</p>
//                   <p><span className="font-bold">Quotation No:</span> {selectedQuotation.quotationNo}</p>
//                 </div>
//               </div>

//               {selectedQuotation.shipTo?.name && (
//                 <div className="mt-4">
//                   <h3 className="font-bold">Ship To:</h3>
//                   <p>{selectedQuotation.shipTo.name}</p>
//                   <p className="whitespace-pre-line">{selectedQuotation.shipTo.address}</p>
//                   <p>Email: {selectedQuotation.shipTo.email}</p>
//                   <p>Phone: {selectedQuotation.shipTo.phone}</p>
//                 </div>
//               )}
//             </div>

//             <div className="overflow-x-auto mb-4">
//               <table className="w-full border-collapse">
//                 <thead>
//                   <tr className="bg-gray-100">
//                     <th className="p-2 border text-left">#</th>
//                     <th className="p-2 border text-left">Product</th>
//                     <th className="p-2 border text-center">Qty</th>
//                     <th className="p-2 border text-center">Unit</th>
//                     <th className="p-2 border text-center">MRP</th>
//                     <th className="p-2 border text-center">Disc1</th>
//                     <th className="p-2 border text-center">Disc2</th>
//                     <th className="p-2 border text-right">Rate</th>
//                     <th className="p-2 border text-right">Amount</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {selectedQuotation.products.map((product, index) => (
//                     <tr key={index} className="border-b">
//                       <td className="p-2 border text-center">{index + 1}</td>
//                       <td className="p-2 border">
//                         {product.name}
//                         {product.size && <div className="text-xs text-gray-500">{product.size}</div>}
//                       </td>
//                       <td className="p-2 border text-center">{product.qty}</td>
//                       <td className="p-2 border text-center">{product.unit}</td>
//                       <td className="p-2 border text-center">{product.mrp.toFixed(2)}</td>
//                       <td className="p-2 border text-center">{product.discount1}%</td>
//                       <td className="p-2 border text-center">{product.discount2}%</td>
//                       <td className="p-2 border text-right">{product.rate.toFixed(2)}</td>
//                       <td className="p-2 border text-right">{product.amount.toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>

//             <div className="flex justify-end mb-4">
//               <div className="w-64">
//                 <div className="flex justify-between py-1">
//                   <span className="font-bold">Subtotal:</span>
//                   <span>₹{calculateSubtotal(selectedQuotation.products).toFixed(2)}</span>
//                 </div>
//                 <div className="flex justify-between py-1">
//                   <span className="font-bold">GST {selectedQuotation.totalgstPercentage}%:</span>
//                   <span>₹{calculateGst(selectedQuotation.products, selectedQuotation.totalgstPercentage).toFixed(2)}</span>
//                 </div>
//                 {selectedQuotation.transportation > 0 && (
//                   <div className="flex justify-between py-1">
//                     <span className="font-bold">Transportation:</span>
//                     <span>₹{selectedQuotation.transportation.toFixed(2)}</span>
//                   </div>
//                 )}
//                 <div className="flex justify-between py-1 border-t border-gray-300 font-bold">
//                   <span>Total:</span>
//                   <span>₹{calculateTotal(selectedQuotation).toFixed(2)}</span>
//                 </div>
//               </div>
//             </div>

//             {selectedQuotation.note && (
//               <div className="mb-4">
//                 <p className="font-bold">Note:</p>
//                 <p>{selectedQuotation.note}</p>
//               </div>
//             )}

//             <div className="mb-4">
//               <h3 className="font-bold">Terms & Conditions:</h3>
//               <ul className="list-disc pl-5">
//                 {selectedQuotation.terms.map((term, index) => (
//                   <li key={index} className="py-1">{term}</li>
//                 ))}
//               </ul>
//             </div>

//             <div>
//               <h3 className="font-bold">Bank Details:</h3>
//               <p>Payment favoring to: "{selectedQuotation.bankDetails?.name || 'N/A'}"</p>
//               <p>Bank Name: CENTRAL BANK OF INDIA</p>
//               <p>A/C No.: {selectedQuotation.bankDetails?.accountNo || 'N/A'}</p>
//               <p>Branch: {selectedQuotation.bankDetails?.branch || 'N/A'}</p>
//               <p>IFSC Code: {selectedQuotation.bankDetails?.ifsc || 'N/A'}</p>
//             </div>

//             <div className="mt-6 flex justify-end">
//               <button
//                 onClick={() => setIsModalOpen(false)}
//                 className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         ) : (
//           <div className="p-6 text-center">Loading quotation details...</div>
//         )}
//       </Modal>
//     </div>
//   );
// };

// export default QuotationHistory;
// // src/pages/QuotationHistory.tsx
// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import { Eye, Download, Edit, Repeat } from "lucide-react";
// import { toast } from "react-hot-toast";

// interface Quotation {
//   _id: string;
//   quotationNo: string;
//   date: string;
//   billTo: {
//     name: string;
//   };
//   products: any[];
//   type?: string; // 'quotation' | 'pi'
// }

// const QuotationHistory: React.FC = () => {
//   const [quotations, setQuotations] = useState<Quotation[]>([]);
//   const navigate = useNavigate();

//   const fetchQuotations = async () => {
//     try {
//       const token = localStorage.getItem("token");
//       const res = await axios.get("https://api.mangrishprince.com/api/v1/quotations", {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setQuotations(res.data);
//     } catch (err) {
//       console.error("Failed to fetch quotations", err);
//     }
//   };

//   useEffect(() => {
//     fetchQuotations();
//   }, []);

//   const handleDownloadPDF = async (id: string) => {
//     try {
//       const token = localStorage.getItem("token");
//       const res = await axios.get(
//         `https://api.mangrishprince.com/api/v1/quotations/${id}/pdf`,
//         {
//           responseType: "blob",
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       const blob = new Blob([res.data], { type: "application/pdf" });
//       const link = document.createElement("a");
//       link.href = window.URL.createObjectURL(blob);
//       link.download = `Quotation_${id}.pdf`;
//       link.click();
//     } catch (err) {
//       console.error("Failed to download PDF", err);
//     }
//   };

//   const handleConvertToPI = async (id: string) => {
//     if (!window.confirm("Convert this quotation to Proforma Invoice?")) return;

//     try {
//       const token = localStorage.getItem("token");
//       await axios.put(
//         // `https://api.mangrishprince.com/api/v1/quotations/${id}/convert-to-pi`,
//         `http://localhost:5000/api/v1/quotations/${id}/convert-to-pi`,
//         {},
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       toast.success("Converted to Proforma Invoice");
//       fetchQuotations();
//     } catch (err) {
//       toast.error("Conversion failed");
//       console.error(err);
//     }
//   };

//   return (
//     <div className="max-w-7xl mx-auto px-4 py-6">
//       <h1 className="text-2xl font-bold mb-6">Proforma Invoice (PI) / Quotation History</h1>

//       <div className="overflow-x-auto">
//         <table className="w-full table-auto border border-gray-300">
//           <thead className="bg-gray-100">
//             <tr>
//               <th className="p-3 border">#</th>
//               <th className="p-3 border">Quotation No</th>
//               <th className="p-3 border">Customer</th>
//               <th className="p-3 border">Date</th>
//               <th className="p-3 border">Toal Amount</th>
//               <th className="p-3 border">Type</th>
//               <th className="p-3 border">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {quotations.map((q, i) => (
//               <tr key={q._id} className="text-sm">
//                 <td className="p-3 border text-center">{i + 1}</td>
//                 <td className="p-3 border">{q.quotationNo}</td>
//                 <td className="p-3 border">{q.billTo?.name}</td>
//                 <td className="p-3 border">{new Date(q.date).toLocaleDateString()}</td>
//                 <td className="p-3 border text-center">{q.products.length}</td>
//                 <td className="p-3 border">
//                   <span
//                     className={`px-2 py-1 rounded text-xs font-semibold ${
//                       q.type === "pi" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"
//                     }`}
//                   >
//                     {q.type === "pi" ? "Proforma Invoice" : "Quotation"}
//                   </span>
//                 </td>
//                 <td className="p-3 border space-x-2">
//                   <button
//                     onClick={() => navigate(`/quotation/${q._id}`)}
//                     className="text-blue-600 hover:text-blue-800"
//                     title="View"
//                   >
//                     <Eye size={16} />
//                   </button>

//                   <button
//                     onClick={() => handleDownloadPDF(q._id)}
//                     className="text-green-600 hover:text-green-800"
//                     title="Download PDF"
//                   >
//                     <Download size={16} />
//                   </button>

//                   <button
//                     onClick={() => navigate(`/quotation/edit/${q._id}`)}
//                     className="text-gray-700 hover:text-black"
//                     title="Edit"
//                   >
//                     <Edit size={16} />
//                   </button>

//                   {q.type !== "pi" && (
//                     <button
//                       onClick={() => handleConvertToPI(q._id)}
//                       className="text-orange-500 hover:text-orange-700"
//                       title="Convert to PI"
//                     >
//                       <Repeat size={16} />
//                     </button>
//                   )}
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>

//         {quotations.length === 0 && (
//           <p className="text-center text-gray-500 py-8">No quotations found.</p>
//         )}
//       </div>
//     </div>
//   );
// };

// export default QuotationHistory;
